/**
 * skelin.js
 * Skelin brand JavaScript — minimal, vanilla, performance-first
 * Handles: scroll-reveal, size guide, gift note toggle, cart bar
 */

'use strict';

/* ============================================================
   SCROLL-TRIGGERED REVEAL
   Elements with data-sk-reveal become visible as they enter viewport.
   Uses IntersectionObserver — no scroll events.
   ============================================================ */
(function initReveal() {
  if (typeof IntersectionObserver === 'undefined') return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    {
      threshold: 0.15,
      rootMargin: '0px 0px -40px 0px',
    }
  );

  function observeAll() {
    document.querySelectorAll('[data-sk-reveal]').forEach((el) => {
      observer.observe(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeAll);
  } else {
    observeAll();
  }

  // Re-observe after Shopify section renders in theme editor
  document.addEventListener('shopify:section:load', function () {
    document.querySelectorAll('[data-sk-reveal]:not(.is-visible)').forEach((el) => {
      observer.observe(el);
    });
  });
})();


/* ============================================================
   GIFT NOTE TOGGLE
   Reveals a gift message textarea when user clicks the toggle button.
   ============================================================ */
(function initGiftNote() {
  document.addEventListener('click', function (e) {
    const toggle = e.target.closest('[data-sk-gift-toggle]');
    if (!toggle) return;

    const targetId = toggle.getAttribute('data-sk-gift-toggle');
    const field = document.getElementById(targetId);
    if (!field) return;

    const isOpen = field.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', isOpen.toString());
  });
})();


/* ============================================================
   STICKY MOBILE ATC
   Shows a sticky add-to-cart bar when the native ATC button
   scrolls out of view on mobile.
   ============================================================ */
(function initStickyAtc() {
  const stickyBar = document.querySelector('[data-sk-sticky-atc]');
  const mainAtc = document.querySelector('[data-sk-main-atc]');

  if (!stickyBar || !mainAtc) return;
  if (typeof IntersectionObserver === 'undefined') return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          stickyBar.classList.add('sk-sticky-atc--visible');
        } else {
          stickyBar.classList.remove('sk-sticky-atc--visible');
        }
      });
    },
    { threshold: 0 }
  );

  observer.observe(mainAtc);
})();


/* ============================================================
   PDP SECONDARY ADD TO CART
   Lets lower-page editorial CTAs use the real Shopify product form.
   ============================================================ */
(function initPdpSecondaryAtc() {
  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-sk-pdp-atc-trigger]');
    if (!trigger) return;

    const productForm =
      document.querySelector('[data-testid="product-information-details"] product-form-component[data-product-id]') ||
      document.querySelector('product-form-component[data-product-id]');
    const addButton = productForm && productForm.querySelector('[ref="addToCartButton"]');

    if (addButton && !addButton.disabled) {
      addButton.click();
      return;
    }

    const productInfo = document.querySelector('[data-testid="product-information-details"]');
    if (productInfo) {
      productInfo.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
})();


/* ============================================================
   CART SHIPPING PROGRESS BAR
   Updates the free-shipping progress bar in the cart drawer.
   Listens for Horizon's cart:updated event.
   ============================================================ */
(function initShippingBar() {
  const THRESHOLD_SEK = 999 * 100; // in cents

  function updateBar(cartTotal) {
    const bars = document.querySelectorAll('[data-sk-shipping-fill]');
    const texts = document.querySelectorAll('[data-sk-shipping-text]');

    if (!bars.length && !texts.length) return;

    const remaining = Math.max(0, THRESHOLD_SEK - cartTotal);
    const pct = Math.min(100, (cartTotal / THRESHOLD_SEK) * 100);

    bars.forEach((bar) => {
      bar.style.width = pct + '%';
    });

    texts.forEach((text) => {
      if (remaining <= 0) {
        text.textContent = 'Du har fri frakt!';
      } else {
        const remainingSek = Math.ceil(remaining / 100);
        text.textContent = `${remainingSek} kr kvar till fri frakt`;
      }
    });
  }

  // Listen for Horizon cart events
  document.addEventListener('cart:updated', function (e) {
    if (e.detail && e.detail.cart) {
      updateBar(e.detail.cart.total_price);
    }
  });

  // Initial load — read from Shopify cart data if available
  if (typeof window.Shopify !== 'undefined' && window.Shopify.cart) {
    updateBar(window.Shopify.cart.total_price);
  }
})();


/* ============================================================
   MARQUEE PAUSE ON FOCUS
   Pauses the trust-strip marquee animation when a child is focused.
   ============================================================ */
(function initMarqueeFocus() {
  document.querySelectorAll('.sk-marquee-strip__inner').forEach((marquee) => {
    marquee.addEventListener('focusin', () => {
      marquee.style.animationPlayState = 'paused';
    });
    marquee.addEventListener('focusout', () => {
      marquee.style.animationPlayState = '';
    });
  });
})();


/* ============================================================
   PRODUCT CARD — IMAGE HOVER SWAP
   Swaps to a second image on hover for cards that have
   data-sk-hover-image set on the card element.
   ============================================================ */
(function initCardHover() {
  if (window.matchMedia('(hover: none)').matches) return;

  document.querySelectorAll('[data-sk-hover-image]').forEach((card) => {
    const gallery = card.querySelector('.sk-card-image');
    if (!gallery) return;

    const primaryImg = gallery.querySelector('img');
    if (!primaryImg) return;

    const hoverSrc = card.getAttribute('data-sk-hover-image');
    if (!hoverSrc) return;

    // Preload hover image
    const hoverImg = new Image();
    hoverImg.src = hoverSrc;
    hoverImg.alt = primaryImg.alt;
    hoverImg.className = primaryImg.className + ' sk-card-image__hover-img';
    hoverImg.style.cssText =
      'position:absolute;inset:0;opacity:0;transition:opacity 350ms ease;will-change:opacity;';

    primaryImg.style.cssText += 'transition:opacity 350ms ease;will-change:opacity;';
    gallery.style.position = 'relative';
    gallery.appendChild(hoverImg);

    card.addEventListener('mouseenter', () => {
      primaryImg.style.opacity = '0';
      hoverImg.style.opacity = '1';
    });

    card.addEventListener('mouseleave', () => {
      primaryImg.style.opacity = '1';
      hoverImg.style.opacity = '0';
    });
  });
})();
